import PropTypes from "prop-types";
import { memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  availableOverlays,
  buildChartRows,
  buildXTicks,
  TOTAL_MAX,
  TOTAL_MIN,
} from "../../../../utils/readiness-report";
import {
  LegendItem,
  OverlayDot,
  OverlaySwitch,
  ScoreDot,
} from "./ReadinessChartParts";
import { ReadinessChartTooltip } from "./ReadinessChartTooltip";
import {
  BAND_COLOR,
  BELOW_COLOR,
  MUTED_COLOR,
  OVERLAY_COLORS,
  SCORE_COLOR,
} from "./readinessChartTheme";
import {
  formatUtcDay,
  sectionTranslator,
  useIntensity,
} from "./readinessFormat";

const CHART_HEIGHT = 300;
const Y_TICKS = [5, 10, 15, 20, 25];
const LONG_PERIOD_DAYS = 365;
const RPE_TICKS = [0, 2, 4, 6, 8, 10];

const tr = sectionTranslator("chart");

// "Session RPE (right axis)", "HRV, ms (right axis)": the unit lives in the legend, the axis is bare numbers
const overlayLegend = (t, overlay, context) => {
  const name = tr(t, `overlays.${overlay}`, { context });
  if (overlay === "sessionRpe") return tr(t, "legend.rightAxis", { name });
  return tr(t, "legend.rightAxisUnit", {
    name,
    unit: tr(t, `units.${overlay}`),
  });
};

export const ReadinessScoreChart = memo(({ report, athleteName }) => {
  const { t } = useTranslation("athlete-portal");
  const { context, toMeasure } = useIntensity();
  const rows = useMemo(
    () =>
      buildChartRows(report).map((row) => ({
        ...row,
        sessionRpe: toMeasure(row.sessionRpe),
      })),
    [report, toMeasure]
  );
  const xTicks = useMemo(() => buildXTicks(rows.length), [rows.length]);
  const overlays = useMemo(() => availableOverlays(report), [report]);
  const [selected, setSelected] = useState("none");
  const overlay = overlays.includes(selected) ? selected : "none";
  const overlayColor = OVERLAY_COLORS[overlay];
  const hasBand = report.normalBand.length > 0;
  const hasScore = rows.some((row) => row.total != null);
  const hasBelow = rows.some((row) => row.status === "below");
  const showHrvSource = report.overlays.hrvSources.length > 1;
  const longPeriod = rows.length > LONG_PERIOD_DAYS;
  const owner = athleteName ? { name: athleteName } : null;

  return (
    <div className="rr-chart">
      <div className="rr-chart__header">
        <div className="rr-chart__heading">
          <h3 className="rr-chart__title">
            {owner ? tr(t, "title", owner) : tr(t, "titleSelf")}
          </h3>
          <p className="rr-chart__description">
            {owner ? tr(t, "description", owner) : tr(t, "descriptionSelf")}
          </p>
        </div>
        {overlays.length > 0 ? (
          <OverlaySwitch
            options={["none", ...overlays]}
            value={overlay}
            onChange={setSelected}
          />
        ) : null}
      </div>
      <ResponsiveContainer width="99%" height={CHART_HEIGHT}>
        <ComposedChart
          data={rows}
          margin={{
            top: 8,
            right: overlay === "none" ? 16 : 8,
            bottom: 0,
            left: 0,
          }}
        >
          <CartesianGrid
            stroke="var(--white-10)"
            strokeDasharray="3 3"
            vertical={false}
          />
          <XAxis
            dataKey="idx"
            type="number"
            domain={[0, Math.max(rows.length - 1, 1)]}
            ticks={xTicks}
            tickFormatter={(idx) =>
              formatUtcDay(
                rows[idx]?.utcDate,
                longPeriod ? "MMM YYYY" : "MMM D"
              )
            }
            tick={{ fontSize: 11, fill: MUTED_COLOR }}
            tickLine={false}
            axisLine={false}
            padding={{ left: 8, right: 8 }}
          />
          <YAxis
            yAxisId="left"
            domain={[TOTAL_MIN, TOTAL_MAX]}
            ticks={Y_TICKS}
            tick={{ fontSize: 11, fill: MUTED_COLOR }}
            tickLine={false}
            axisLine={false}
            width={32}
          />
          {overlay === "none" ? null : (
            <YAxis
              yAxisId="right"
              orientation="right"
              domain={overlay === "sessionRpe" ? [0, 10] : ["auto", "auto"]}
              ticks={overlay === "sessionRpe" ? RPE_TICKS : undefined}
              tick={{ fontSize: 11, fill: overlayColor }}
              tickLine={false}
              axisLine={false}
              width={32}
            />
          )}
          <Tooltip
            content={<ReadinessChartTooltip showHrvSource={showHrvSource} />}
            cursor={{ stroke: MUTED_COLOR, strokeWidth: 1 }}
            isAnimationActive={false}
          />
          <Area
            yAxisId="left"
            dataKey="band"
            stroke="none"
            fill={BAND_COLOR}
            activeDot={false}
            isAnimationActive={false}
          />
          {overlay === "none" ? null : (
            <>
              <Line
                yAxisId="right"
                dataKey={`${overlay}Line`}
                stroke={overlayColor}
                strokeWidth={1.5}
                strokeDasharray="4 3"
                dot={false}
                activeDot={false}
                isAnimationActive={false}
              />
              <Line
                yAxisId="right"
                dataKey={overlay}
                stroke="none"
                dot={<OverlayDot color={overlayColor} />}
                activeDot={<OverlayDot color={overlayColor} radius={5} />}
                isAnimationActive={false}
              />
            </>
          )}
          <Line
            yAxisId="left"
            dataKey="scoreLine"
            stroke={SCORE_COLOR}
            strokeWidth={2}
            dot={false}
            activeDot={false}
            isAnimationActive={false}
          />
          <Line
            yAxisId="left"
            dataKey="total"
            stroke="none"
            dot={<ScoreDot />}
            activeDot={{ r: 5, fill: SCORE_COLOR, stroke: "none" }}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
      <div className="rr-legend">
        {hasScore ? (
          <LegendItem
            kind="line"
            color={SCORE_COLOR}
            label={tr(t, "legend.score")}
          />
        ) : null}
        {hasBand ? (
          <LegendItem
            kind="square"
            color={BAND_COLOR}
            label={
              owner ? tr(t, "legend.normal", owner) : tr(t, "legend.normalSelf")
            }
          />
        ) : null}
        {hasBelow ? (
          <LegendItem
            kind="dot"
            color={BELOW_COLOR}
            label={tr(t, "legend.below")}
          />
        ) : null}
        {overlay === "none" ? null : (
          <LegendItem
            kind="dashed"
            color={overlayColor}
            label={overlayLegend(t, overlay, context)}
          />
        )}
      </div>
    </div>
  );
});

ReadinessScoreChart.propTypes = {
  report: PropTypes.shape({
    normalBand: PropTypes.array.isRequired,
    overlays: PropTypes.object.isRequired,
  }).isRequired,
  athleteName: PropTypes.string,
};
